import { Box, LinearProgress, Stack } from '@mui/material';

import { useState, useCallback } from 'react';

import { useDropzone } from 'react-dropzone';

import { cross, documentIcon, greenTick } from 'common/assets/images';
import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';

import styles from './FileDrop.module.scss';

interface IFileDropProps {
	handleFileData: (fileData: any, errorMessage?: string) => void;
	fileData: any;
	isLoading: boolean;
	accept?: any;
	progress?: number;
	progressActive?: boolean;
	setProgressActive?: any;
	isSuccess?: boolean;
	maxfileSize?: number; // in MB
	setShowStartProcessingButton?: any;
	dataType?: string;
	hideFileDrop?: any;
}

const FileDrop = (props: IFileDropProps) => {
	const {
		handleFileData,
		fileData,
		accept,
		progress,
		progressActive,
		setProgressActive,
		isSuccess,
		maxfileSize,
		dataType,
		setShowStartProcessingButton,
		hideFileDrop,
	} = props;

	const [errors, setErrors] = useState('');
	const [errorToast, setErrorToast] = useState(false);

	const filesize = fileData[0]?.size;
	const fileSizeMB = filesize / 1048576;
	const fileSizeKB = filesize / 1024;

	const formattedFileSize =
		fileSizeMB < 1
			? `${fileSizeKB.toFixed(2)}KB`
			: `${fileSizeMB.toFixed(2)}MB`;

	const deleteHandler = useCallback(() => {
		handleFileData([]);
		if (hideFileDrop) hideFileDrop();
		if (setProgressActive) setProgressActive(false);
		if (setShowStartProcessingButton) setShowStartProcessingButton(true);
	}, []);

	const onDrop = useCallback((acceptedFiles: any, rejectedFiles: any) => {
		if (acceptedFiles?.length) {
			handleFileData(acceptedFiles);
		}

		if (rejectedFiles?.length) {
			let errorMessage = rejectedFiles[0].errors[0].message;
			if (rejectedFiles[0].errors[0].code === 'file-too-large' && maxfileSize) {
				errorMessage = `File is larger than ${maxfileSize} MB`;
			}
			setErrors(errorMessage);
			handleFileData([], errorMessage);
		}
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: accept || { 'text/csv': [] },
		...(maxfileSize && { maxSize: maxfileSize * 1048576 }),
		onDrop,
		multiple: false,
	});

	const handleToast = useCallback(() => setErrorToast(false), []);

	const onclickX = () => {
		if (!isSuccess) deleteHandler();
	};

	const renderStatusIcon = () => {
		const src = progress === 100 ? greenTick : cross;
		const alt = isSuccess ? 'tick' : 'x';
		return (
			<DeleteIcon
				onClick={onclickX}
				className={styles['filedrop-cross-icon']}
				data-testid="DeleteIcon"
			>
				<img src={src} alt={alt} />
			</DeleteIcon>
		);
	};

	return (
		<>
			{fileData && fileData.length > 0 ? (
				<Box
					className={styles['filedrop-filename-container']}
					data-testid="filedrop-filename-container"
				>
					<Stack
						direction="column"
						className={styles['filedrop-filenamediv-container']}
					>
						<Stack
							direction="row"
							className={styles['filedrop-filename-stack']}
							spacing={1}
						>
							<Stack
								direction="row"
								spacing={1}
								className={styles['filedrop-filename-img-stack']}
							>
								<img src={documentIcon} alt="doc" />
								{fileData && (
									<Stack
										direction="row"
										spacing={1}
										className={styles['filedrop-filename-maindiv']}
									>
										<div className={styles['filedrop-filename']}>
											{fileData[0]?.name}
										</div>
										<div className={styles['filedrop-filesize']}>
											{formattedFileSize}
										</div>
									</Stack>
								)}
							</Stack>
							{progressActive && progress && progress !== 100 ? (
								<div className={styles['filedrop-progress-text']}>
									{progress.toFixed(0)}%
								</div>
							) : (
								<div className={styles['filedrop-greentick']}>
									{renderStatusIcon()}
								</div>
							)}
						</Stack>
						<div data-testid="progress-bar">
							{progressActive && !isSuccess && (
								<LinearProgress
									variant="determinate"
									value={progress}
									className={styles['filedrop-progressbar']}
								/>
							)}
						</div>
					</Stack>
				</Box>
			) : (
				<Box
					className={styles['filedrop-drop-container']}
					data-testid="filedrop-drop-container"
				>
					<div {...getRootProps()}>
						<input {...getInputProps()} data-testid="dropzone" />
						<div>
							{isDragActive ? (
								<p>Drop the files here ...</p>
							) : (
								<p>
									Drag & drop or{' '}
									<span
										className={
											dataType
												? styles['filedrop-datatype-chooseoption']
												: styles['filedrop-choose']
										}
									>
										choose file
									</span>
								</p>
							)}
						</div>
					</div>
				</Box>
			)}
			<AlertComponent
				open={errorToast}
				toastMessage={errors}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};

export default FileDrop;
